(() => {
  'use strict';

  const FORM_SELECTOR = '[data-vf-webinar-form]';
  const CTA_SELECTOR = '[data-vf-cta]';
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  const dispatchTrackingEvent = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(`vf:webinar:${name}`, { detail }));
    window.dataLayer?.push({ event: `vf_webinar_${name}`, ...detail });
  };

  const readAttribution = () => {
    const params = new URLSearchParams(window.location.search);
    const attribution = {};

    UTM_KEYS.forEach((key) => {
      const incoming = params.get(key);
      if (incoming) {
        try {
          window.sessionStorage.setItem(`vf_webinar_${key}`, incoming);
        } catch (_error) {
        }
      }

      let stored = '';
      try {
        stored = window.sessionStorage.getItem(`vf_webinar_${key}`) || '';
      } catch (_error) {
        stored = '';
      }

      attribution[key] = incoming || stored;
    });

    return attribution;
  };

  const setStatus = (form, message, state = 'error') => {
    const status = form.querySelector('[data-vf-form-status]');
    if (!status) return;

    status.textContent = message;
    status.dataset.state = state;
    status.classList.add('is-visible');
    status.focus({ preventScroll: true });
  };

  const clearFieldError = (field) => {
    field.removeAttribute('aria-invalid');
    const error = document.getElementById(`${field.id}-error`);
    if (error) error.textContent = '';
  };

  const setFieldError = (field, message) => {
    field.setAttribute('aria-invalid', 'true');
    const error = document.getElementById(`${field.id}-error`);
    if (error) error.textContent = message;
  };

  const validateForm = (form) => {
    let valid = true;
    const firstName = form.querySelector('[data-vf-field="first_name"]');
    const email = form.querySelector('[data-vf-field="email"]');
    const privacy = form.querySelector('[data-vf-field="privacy"]');
    const marketingConsent = form.querySelector('[data-vf-field="marketing_consent"]');

    [firstName, email, privacy, marketingConsent].forEach((field) => field && clearFieldError(field));

    if (!firstName?.value.trim()) {
      setFieldError(firstName, 'Inserisci il tuo nome.');
      valid = false;
    }

    if (!email?.value.trim()) {
      setFieldError(email, 'Inserisci la tua email.');
      valid = false;
    } else if (!email.validity.valid) {
      setFieldError(email, 'Controlla il formato dell’email.');
      valid = false;
    }

    if (!privacy?.checked) {
      setFieldError(privacy, 'Conferma di aver letto l’informativa privacy.');
      valid = false;
    }

    if (!marketingConsent?.checked) {
      setFieldError(marketingConsent, 'Conferma il consenso email per completare l’iscrizione.');
      valid = false;
    }

    if (!valid) {
      form.querySelector('[aria-invalid="true"]')?.focus();
    }

    return valid;
  };

  const collectPayload = (form) => {
    const attribution = readAttribution();
    return {
      first_name: form.querySelector('[data-vf-field="first_name"]').value.trim(),
      email: form.querySelector('[data-vf-field="email"]').value.trim(),
      source: form.dataset.source || 'shopify_webinar_landing',
      landing_url: window.location.href,
      timestamp: new Date().toISOString(),
      marketing_consent: Boolean(form.querySelector('[data-vf-field="marketing_consent"]')?.checked),
      website: form.querySelector('[name="website"]')?.value || '',
      started_at: Number(form.dataset.vfStartedAt || Date.now()),
      ...attribution,
    };
  };

  const prepareForm = (form) => {
    if (form.dataset.vfWebinarPrepared === 'true') return;
    form.dataset.vfWebinarPrepared = 'true';
    form.dataset.vfStartedAt = String(Date.now());

    if (form.querySelector('[data-vf-form-success]')) {
      dispatchTrackingEvent('registration_success', { source: form.dataset.source });

      if (form.dataset.successUrl) {
        window.location.replace(form.dataset.successUrl);
      }

      return;
    }

    let hasStarted = false;
    let isSubmitting = false;

    const attribution = readAttribution();
    Object.entries(attribution).forEach(([key, value]) => {
      const field = form.elements[key];
      if (field) field.value = value;
    });

    if (form.elements.landing_url) form.elements.landing_url.value = window.location.href;

    form.addEventListener('focusin', () => {
      if (hasStarted) return;
      hasStarted = true;
      dispatchTrackingEvent('start_form', { source: form.dataset.source });
    });

    form.addEventListener('input', (event) => {
      if (event.target instanceof HTMLInputElement) clearFieldError(event.target);
    });

    form.addEventListener('submit', async (event) => {
      if (isSubmitting) {
        event.preventDefault();
        return;
      }

      if (!validateForm(form)) {
        event.preventDefault();
        return;
      }

      isSubmitting = true;
      const button = form.querySelector('[type="submit"]');
      const idleLabel = button?.dataset.idleLabel || button?.textContent || '';
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.textContent = button.dataset.loadingLabel || 'Invio in corso…';
      }

      const payload = collectPayload(form);
      dispatchTrackingEvent('submit_form', {
        source: payload.source,
        marketing_consent: payload.marketing_consent,
      });

      const integrationEvent = new CustomEvent('vf:webinar:integration-submit', {
        bubbles: true,
        cancelable: true,
        detail: { form, payload },
      });
      const handledByIntegration = !form.dispatchEvent(integrationEvent);

      if (handledByIntegration) {
        event.preventDefault();
        return;
      }

      const endpoint = form.dataset.integrationEndpoint;
      if (endpoint) {
        event.preventDefault();
        const registrationKey = `vf_zoom_registered_${payload.email.toLowerCase()}`;
        let alreadyRegistered = false;
        try {
          alreadyRegistered = window.sessionStorage.getItem(registrationKey) === 'true';
        } catch (_error) {
          alreadyRegistered = false;
        }

        try {
          if (!alreadyRegistered) {
            const response = await fetch(endpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error(`integration_${response.status}`);
            try {
              window.sessionStorage.setItem(registrationKey, 'true');
            } catch (_error) {
            }
          }

          HTMLFormElement.prototype.submit.call(form);
          return;
        } catch (_error) {
          isSubmitting = false;
          if (button) {
            button.disabled = false;
            button.removeAttribute('aria-disabled');
            button.textContent = idleLabel;
          }
          setStatus(form, 'Non siamo riusciti a completare l’iscrizione. Riprova tra qualche istante.');
          dispatchTrackingEvent('registration_error', { source: payload.source });
          return;
        }
      }

      window.setTimeout(() => {
        isSubmitting = false;
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-disabled');
          button.textContent = idleLabel;
        }
      }, 5000);
    });
  };

  const initializeWebinarPage = () => {
    document.querySelectorAll(FORM_SELECTOR).forEach(prepareForm);
    document.querySelectorAll(CTA_SELECTOR).forEach((cta) => {
      if (cta.dataset.vfTrackingPrepared === 'true') return;
      cta.dataset.vfTrackingPrepared = 'true';
      cta.addEventListener('click', () => {
        dispatchTrackingEvent('cta_click', { placement: cta.dataset.vfCta || 'unknown' });
      });
    });
    dispatchTrackingEvent('view_landing', { path: window.location.pathname });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeWebinarPage, { once: true });
  } else {
    initializeWebinarPage();
  }
})();
